import { useState, useEffect, useCallback, useRef } from 'react';
import { communityApi } from './communityApi';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  TrendingUp, Upload, CheckCircle, Clock, Loader2, Trash2,
  Copy, Share2, X, ShieldCheck, ImageIcon
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../../components/ui/dialog';

const MiniAvatar = ({ username }) => {
  const colors = ['from-emerald-500/40 to-emerald-900/40', 'from-blue-500/40 to-blue-900/40', 'from-purple-500/40 to-purple-900/40', 'from-amber-500/40 to-amber-900/40'];
  const idx = (username || '').charCodeAt(0) % colors.length;
  return (
    <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${colors[idx]} flex items-center justify-center font-display font-bold text-white text-sm border border-white/10`}>
      {(username || '?')[0]}
    </div>
  );
};

const VerifBadge = ({ status }) => {
  if (status === 'verified') return <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 font-medium flex items-center gap-1" data-testid="badge-verified"><CheckCircle className="w-3 h-3" /> Vérifié CAP OS</span>;
  if (status === 'pending') return <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 font-medium flex items-center gap-1" data-testid="badge-pending"><Clock className="w-3 h-3" /> En attente</span>;
  return null;
};

// ── Shareable Card ──
const ShareableCard = ({ perf, username, onClose }) => {
  const cardRef = useRef(null);

  const handleCopy = async () => {
    try {
      const el = cardRef.current;
      const { default: h2c } = await import('html2canvas');
      const canvas = await h2c(el, { backgroundColor: '#0d0f14', scale: 2 });
      canvas.toBlob(blob => {
        navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        toast.success('Image copiée !');
      });
    } catch { toast.error('Impossible de copier (utilisez un screenshot)'); }
  };

  const shareText = `📈 Mes résultats ${perf.month_label} sur CAP OS:\n+${perf.month_perf}% ce mois | +${perf.ytd_perf}% YTD\n#CapOS #Investissement`;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#141720] border-white/10 text-white max-w-md p-0" data-testid="shareable-card-modal">
        <DialogHeader className="px-6 pt-6 pb-0">
          <DialogTitle className="text-lg font-bold">Carte partageable</DialogTitle>
          <DialogDescription className="text-cap-text-secondary text-sm">Partagez vos résultats</DialogDescription>
        </DialogHeader>
        <div className="px-6 pb-6 space-y-4">
          {/* Card */}
          <div ref={cardRef} className="bg-gradient-to-br from-[#0d0f14] to-[#1a1d27] border border-white/10 rounded-2xl p-6 space-y-3" data-testid="share-card">
            <div className="flex items-center justify-between">
              <span className="text-[#00e5a0] font-display font-bold text-sm">CAP OS</span>
              <span className="text-cap-text-muted text-[10px]">capos.fr</span>
            </div>
            <p className="text-cap-text-secondary text-xs">@{username} — {perf.month_label}</p>
            <div className="space-y-1">
              <p className={`font-display text-3xl font-black ${perf.month_perf >= 0 ? 'text-[#00e5a0]' : 'text-red-400'}`}>
                {perf.month_perf >= 0 ? '+' : ''}{perf.month_perf}% ce mois
              </p>
              <p className={`font-display text-lg font-bold ${perf.ytd_perf >= 0 ? 'text-white/70' : 'text-red-400/70'}`}>
                {perf.ytd_perf >= 0 ? '+' : ''}{perf.ytd_perf}% YTD
              </p>
            </div>
            {perf.verification_status === 'verified' && <VerifBadge status="verified" />}
          </div>
          {/* Actions */}
          <div className="flex items-center gap-2">
            <button onClick={handleCopy} className="flex-1 bg-white/5 border border-white/10 rounded-lg h-9 flex items-center justify-center gap-1.5 text-white text-xs hover:bg-white/10 transition-colors">
              <Copy className="w-3.5 h-3.5" /> Copier l'image
            </button>
            <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noreferrer"
              className="flex-1 bg-[#1DA1F2]/10 border border-[#1DA1F2]/20 rounded-lg h-9 flex items-center justify-center gap-1.5 text-[#1DA1F2] text-xs hover:bg-[#1DA1F2]/20 transition-colors">
              <Share2 className="w-3.5 h-3.5" /> Twitter
            </a>
            <a href={`https://www.linkedin.com/sharing/share-offsite/?url=https://capos.fr`} target="_blank" rel="noreferrer"
              className="flex-1 bg-[#0077B5]/10 border border-[#0077B5]/20 rounded-lg h-9 flex items-center justify-center gap-1.5 text-[#0077B5] text-xs hover:bg-[#0077B5]/20 transition-colors">
              <Share2 className="w-3.5 h-3.5" /> LinkedIn
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ── Publish Form ──
const PublishForm = ({ onClose, onSubmit }) => {
  const [monthPerf, setMonthPerf] = useState('');
  const [ytdPerf, setYtdPerf] = useState('');
  const [comment, setComment] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [requestVerif, setRequestVerif] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5 Mo'); return; }
    const reader = new FileReader();
    reader.onload = () => setScreenshot(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!monthPerf || !ytdPerf) return;
    setSubmitting(true);
    try {
      await onSubmit({
        month_perf: parseFloat(monthPerf),
        ytd_perf: parseFloat(ytdPerf),
        comment: comment.trim() || null,
        screenshot_base64: requestVerif ? screenshot : null,
        request_verification: requestVerif,
      });
      onClose();
    } catch { /* */ }
    finally { setSubmitting(false); }
  };

  return (
    <div className="bg-[#141720] border border-[#00e5a0]/20 rounded-xl p-5 space-y-4" data-testid="publish-perf-form">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-cap-text-secondary mb-1">Performance ce mois (%) <span className="text-red-400">*</span></label>
          <input type="number" step="0.1" className="w-full bg-[#0d0f14] border border-white/10 rounded-lg px-3 h-9 text-white text-sm outline-none focus:border-[#00e5a0]/40"
            placeholder="Ex: 5.2" value={monthPerf} onChange={e => setMonthPerf(e.target.value)} data-testid="input-month-perf" />
        </div>
        <div>
          <label className="block text-xs text-cap-text-secondary mb-1">Performance YTD (%) <span className="text-red-400">*</span></label>
          <input type="number" step="0.1" className="w-full bg-[#0d0f14] border border-white/10 rounded-lg px-3 h-9 text-white text-sm outline-none focus:border-[#00e5a0]/40"
            placeholder="Ex: 12.8" value={ytdPerf} onChange={e => setYtdPerf(e.target.value)} data-testid="input-ytd-perf" />
        </div>
      </div>
      <div>
        <label className="block text-xs text-cap-text-secondary mb-1">Commentaire <span className="text-cap-text-muted">(optionnel, max 300)</span></label>
        <textarea className="w-full bg-[#0d0f14] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none resize-none h-16 placeholder:text-cap-text-muted focus:border-[#00e5a0]/40"
          placeholder="Qu'est-ce qui a marqué ce mois ?" value={comment} onChange={e => setComment(e.target.value.slice(0, 300))} />
      </div>
      {/* Screenshot upload */}
      <div>
        <label className="block text-xs text-cap-text-secondary mb-1">Screenshot courtier <span className="text-cap-text-muted">(pour vérification)</span></label>
        <input type="file" accept="image/*" ref={fileRef} className="hidden" onChange={handleFile} />
        {screenshot ? (
          <div className="relative">
            <img src={screenshot} alt="screenshot" className="w-full h-32 object-cover rounded-lg border border-white/10" />
            <button onClick={() => setScreenshot(null)} className="absolute top-1 right-1 bg-black/60 rounded-full p-1"><X className="w-3 h-3 text-white" /></button>
          </div>
        ) : (
          <button onClick={() => fileRef.current?.click()} className="w-full bg-[#0d0f14] border border-dashed border-white/10 rounded-lg h-20 flex flex-col items-center justify-center gap-1 text-cap-text-muted hover:border-white/20 transition-colors">
            <ImageIcon className="w-5 h-5" /><span className="text-[10px]">Cliquer pour uploader (max 5 Mo)</span>
          </button>
        )}
      </div>
      {/* Verification checkbox */}
      <label className="flex items-center gap-2 cursor-pointer" data-testid="request-verif-checkbox">
        <input type="checkbox" checked={requestVerif} onChange={e => setRequestVerif(e.target.checked)}
          className="w-4 h-4 rounded border-white/20 bg-[#0d0f14] accent-[#00e5a0]" disabled={!screenshot} />
        <span className={`text-xs ${!screenshot ? 'text-cap-text-muted' : 'text-cap-text-secondary'}`}>
          <ShieldCheck className="w-3.5 h-3.5 inline mr-1" />Soumettre à la vérification CAP OS
        </span>
      </label>
      {/* Actions */}
      <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
        <button onClick={onClose} className="text-cap-text-secondary text-xs h-8 px-4">Annuler</button>
        <button onClick={handleSubmit} disabled={!monthPerf || !ytdPerf || submitting}
          className="bg-[#00e5a0] text-[#0d0f14] font-semibold text-xs h-8 px-5 rounded-lg disabled:opacity-40 flex items-center gap-1" data-testid="submit-perf-btn">
          {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TrendingUp className="w-3.5 h-3.5" />} Publier +50 XP
        </button>
      </div>
    </div>
  );
};

// ── Admin Panel ──
const AdminPanel = ({ isAdmin }) => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAdmin) return;
    communityApi.getPendingVerifications().then(setPending).catch(() => {}).finally(() => setLoading(false));
  }, [isAdmin]);

  if (!isAdmin) return null;

  const handleVerify = async (id, action) => {
    try {
      await communityApi.verifyPerformance(id, action);
      setPending(prev => prev.filter(p => p.id !== id));
      toast.success(action === 'approve' ? 'Performance vérifiée !' : 'Performance rejetée');
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="bg-[#141720] border border-amber-500/20 rounded-xl p-5 mb-4" data-testid="admin-panel">
      <h3 className="font-display text-sm font-bold text-amber-300 mb-3 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Panel Admin — Vérifications en attente</h3>
      {loading ? <Loader2 className="w-4 h-4 animate-spin text-cap-text-muted" /> :
        pending.length === 0 ? <p className="text-cap-text-muted text-xs">Aucune vérification en attente.</p> :
          <div className="space-y-3">
            {pending.map(p => (
              <div key={p.id} className="bg-[#0d0f14] rounded-lg p-4 flex items-start gap-4">
                <div className="flex-1">
                  <p className="text-white text-sm font-semibold">{p.author}</p>
                  <p className="text-cap-text-secondary text-xs">Mois: {p.month_perf >= 0 ? '+' : ''}{p.month_perf}% | YTD: {p.ytd_perf >= 0 ? '+' : ''}{p.ytd_perf}%</p>
                  {p.comment && <p className="text-cap-text-muted text-xs italic mt-1">{p.comment}</p>}
                  {p.screenshot_base64 && <img src={p.screenshot_base64} alt="screenshot" className="mt-2 w-full max-h-48 object-contain rounded-lg border border-white/10" />}
                </div>
                <div className="flex flex-col gap-2">
                  <button onClick={() => handleVerify(p.id, 'approve')} className="bg-emerald-500/15 text-emerald-400 text-xs h-8 px-3 rounded-lg hover:bg-emerald-500/25" data-testid={`approve-${p.id}`}>Approuver</button>
                  <button onClick={() => handleVerify(p.id, 'reject')} className="bg-red-500/15 text-red-400 text-xs h-8 px-3 rounded-lg hover:bg-red-500/25" data-testid={`reject-${p.id}`}>Rejeter</button>
                </div>
              </div>
            ))}
          </div>
      }
    </div>
  );
};

// ══════════════════════════════════════
// MAIN RESULTS TAB
// ══════════════════════════════════════
const ResultsTab = ({ username, isAdmin, onXpChange }) => {
  const [perfs, setPerfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [sharePerf, setSharePerf] = useState(null);

  const load = useCallback(async () => {
    try { setPerfs(await communityApi.listPerformances()); }
    catch { /* */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handlePublish = async (data) => {
    const p = await communityApi.publishPerformance(data);
    setPerfs(prev => [p, ...prev]);
    onXpChange?.(75);
    toast.success('+50 XP — Résultat publié !');
    setShowForm(false);
  };

  const handleDelete = async (id) => {
    try { await communityApi.deletePerformance(id); setPerfs(prev => prev.filter(p => p.id !== id)); toast.success('Supprimé'); }
    catch { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-5" data-testid="results-tab">
      <AdminPanel isAdmin={isAdmin} />

      <div className="flex items-center justify-between">
        <p className="text-cap-text-secondary text-sm">Résultats publiés par les membres</p>
        <button onClick={() => setShowForm(!showForm)} className="bg-[#00e5a0] text-[#0d0f14] font-semibold text-xs h-8 px-4 rounded-lg flex items-center gap-1.5" data-testid="publish-perf-btn">
          <TrendingUp className="w-3.5 h-3.5" /> Publier mes résultats <span className="opacity-70">+50 XP</span>
        </button>
      </div>

      {showForm && <PublishForm onClose={() => setShowForm(false)} onSubmit={handlePublish} />}

      {loading && <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cap-text-muted" /></div>}

      {!loading && perfs.length === 0 && <p className="text-cap-text-muted text-sm text-center py-12">Aucun résultat publié encore.</p>}

      {perfs.map((p, i) => (
        <motion.div key={p.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 5) * 0.04 }}
          className="bg-[#1a1d27] border border-white/5 rounded-xl p-5 flex items-center gap-4" data-testid={`perf-${p.id}`}>
          <div className="text-center font-display text-lg font-bold text-cap-text-muted w-8">#{i + 1}</div>
          <MiniAvatar username={p.author} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <p className="text-white font-semibold text-sm">{p.author}</p>
              {p.is_owner && <span className="text-[8px] px-1 py-0.5 rounded bg-[#00e5a0]/10 text-[#00e5a0] font-bold">VOUS</span>}
              <VerifBadge status={p.verification_status} />
            </div>
            <p className="text-cap-text-muted text-xs">{p.month_label}</p>
            {p.comment && <p className="text-cap-text-muted text-xs italic line-clamp-1 mt-0.5">{p.comment}</p>}
          </div>
          <div className="flex items-center gap-4">
            <div className="text-center">
              <p className={`font-display text-xl font-bold ${p.month_perf >= 0 ? 'text-[#00e5a0]' : 'text-red-400'}`}>
                {p.month_perf >= 0 ? '+' : ''}{p.month_perf}%
              </p>
              <p className="text-cap-text-muted text-[10px]">Ce mois</p>
            </div>
            <div className="text-center">
              <p className={`font-display text-base font-bold ${p.ytd_perf >= 0 ? 'text-white/70' : 'text-red-400/70'}`}>
                {p.ytd_perf >= 0 ? '+' : ''}{p.ytd_perf}%
              </p>
              <p className="text-cap-text-muted text-[10px]">YTD</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setSharePerf(p)} className="p-1.5 rounded-lg hover:bg-white/5 text-cap-text-muted hover:text-white" title="Partager">
              <Share2 className="w-4 h-4" />
            </button>
            {p.is_owner && (
              <button onClick={() => handleDelete(p.id)} className="p-1.5 rounded-lg hover:bg-red-400/10 text-cap-text-muted hover:text-red-400" title="Supprimer">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </motion.div>
      ))}

      {sharePerf && <ShareableCard perf={sharePerf} username={username} onClose={() => setSharePerf(null)} />}
    </div>
  );
};

export default ResultsTab;
